import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ExportJob,
  ExportJobType,
  startExport,
  getExportStatus,
  cancelExport,
  getRecentExports
} from '../api/invoiceApi';

interface UseExportJobOptions {
  pollingInterval?: number; // ms, default 2000
  onComplete?: (job: ExportJob) => void;
  onError?: (error: string) => void;
}

interface UseExportJobReturn {
  currentJob: ExportJob | null;
  recentJobs: ExportJob[];
  isExporting: boolean;
  isLoading: boolean;
  error: string | null;
  start: (startDate: Date, endDate: Date, jobType?: ExportJobType) => Promise<void>;
  cancel: () => Promise<void>;
  downloadFile: () => void;
  refresh: () => Promise<void>;
}

export function useExportJob(options: UseExportJobOptions = {}): UseExportJobReturn {
  const { pollingInterval = 2000, onComplete, onError } = options;

  const [currentJob, setCurrentJob] = useState<ExportJob | null>(null);
  const [recentJobs, setRecentJobs] = useState<ExportJob[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Check if job is in progress
  const isExporting = currentJob !== null &&
    (currentJob.status === 'PENDING' || currentJob.status === 'PROCESSING');

  // Stop polling
  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  // Poll for job status
  const pollJobStatus = useCallback(async (jobId: number) => {
    try {
      const job = await getExportStatus(jobId);
      setCurrentJob(job);

      if (job.status === 'COMPLETED') {
        stopPolling();
        onComplete?.(job);
      } else if (job.status === 'FAILED') {
        stopPolling();
        setError(job.errorMessage || 'Export failed');
        onError?.(job.errorMessage || 'Export failed');
      } else if (job.status === 'CANCELLED') {
        stopPolling();
      }
    } catch (err) {
      console.error('Failed to poll export status:', err);
    }
  }, [stopPolling, onComplete, onError]);

  // Start export job
  const start = useCallback(async (
    startDate: Date,
    endDate: Date,
    jobType: ExportJobType = 'INVOICE_EXPORT'
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const job = await startExport({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        jobType
      });

      setCurrentJob(job);

      // Start polling for status updates
      pollingRef.current = setInterval(() => {
        pollJobStatus(job.id);
      }, pollingInterval);

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to start export';
      setError(message);
      onError?.(message);
    } finally {
      setIsLoading(false);
    }
  }, [pollingInterval, pollJobStatus, onError]);

  // Cancel export job
  const cancel = useCallback(async () => {
    if (!currentJob) return;

    try {
      await cancelExport(currentJob.id);
      stopPolling();
      setCurrentJob(prev => prev ? { ...prev, status: 'CANCELLED' } : null);
    } catch (err) {
      console.error('Failed to cancel export:', err);
    }
  }, [currentJob, stopPolling]);

  // Download completed file
  const downloadFile = useCallback(() => {
    if (!currentJob?.fileUrl) return;

    // Create download link
    const link = document.createElement('a');
    link.href = `/api${currentJob.fileUrl}`;
    link.download = currentJob.fileName || 'export.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [currentJob]);

  // Refresh recent jobs
  const refresh = useCallback(async () => {
    try {
      const jobs = await getRecentExports();
      setRecentJobs(jobs);

      // Check if there's an active job
      const activeJob = jobs.find(j => j.status === 'PENDING' || j.status === 'PROCESSING');
      if (activeJob) {
        setCurrentJob(activeJob);
        // Start polling for this job
        pollingRef.current = setInterval(() => {
          pollJobStatus(activeJob.id);
        }, pollingInterval);
      }
    } catch (err) {
      console.error('Failed to fetch recent exports:', err);
    }
  }, [pollJobStatus, pollingInterval]);

  // Load recent jobs on mount
  useEffect(() => {
    refresh();

    return () => {
      stopPolling();
    };
  }, [refresh, stopPolling]);

  return {
    currentJob,
    recentJobs,
    isExporting,
    isLoading,
    error,
    start,
    cancel,
    downloadFile,
    refresh
  };
}

export default useExportJob;
